/**
 * Task Type Configuration
 *
 * Single source of truth for task types: icons, labels, colors, descriptions
 * and translation keys. Used throughout the UI for consistent task type
 * representation.
 *
 * Colors follow the brand palette only (see COLOR-SYSTEM-GUIDE.md):
 * - Informative / app identity: dark blue `#233974`
 * - Constructive: green `#608d33`
 * - Destructive: red `#c5452b`
 * Amber/yellow is intentionally avoided for non-blocking task-type accents.
 */

import { TaskType } from '@shared/models/task-type.enum';

/**
 * Configuration for a task type
 */
export interface TaskTypeConfig {
  /** Icon name from Ant Design icons (without 'Outline' suffix) */
  icon: string;
  /** Human-readable label (fallback when no translation is available) */
  label: string;
  /** i18n key resolving to the localized type label, e.g. `taskType.GENERAL` */
  translationKey: string;
  /** Primary color for the task type (brand-compliant CSS color value) */
  color: string;
  /** Background/soft color for badges and highlights */
  softColor: string;
  /** Short description of the task type */
  description: string;
}

/**
 * Configuration map for all task types
 */
export const TASK_TYPE_CONFIG: Record<TaskType, TaskTypeConfig> = {
  [TaskType.GENERAL]: {
    icon: 'check-square',
    label: 'General',
    translationKey: 'taskType.GENERAL',
    color: '#233974', // brand blue (informative)
    softColor: 'rgba(35, 57, 116, 0.12)',
    description: 'Standard task',
  },
  [TaskType.GEOPOINT]: {
    icon: 'environment',
    label: 'GeoPoint',
    translationKey: 'taskType.GEOPOINT',
    color: '#608d33', // brand green (constructive)
    softColor: 'rgba(96, 141, 51, 0.12)',
    description: 'Single location',
  },
  [TaskType.MAP]: {
    icon: 'compass',
    label: 'Map',
    translationKey: 'taskType.MAP',
    color: '#233974', // brand blue (informative); amber is not part of the palette
    softColor: 'rgba(35, 57, 116, 0.08)',
    description: 'Area coverage',
  },
};

/**
 * Normalize an arbitrary task type string into a valid {@link TaskType}.
 * Falls back to {@link TaskType.GENERAL} for unknown/empty values.
 */
export function normalizeTaskType(type: string | null | undefined): TaskType {
  if (!type) {
    return TaskType.GENERAL;
  }

  const upperType = type.toUpperCase() as TaskType;
  return upperType in TASK_TYPE_CONFIG ? upperType : TaskType.GENERAL;
}

/**
 * Get the configuration for a task type
 * Returns GENERAL config as fallback for unknown types
 */
export function getTaskTypeConfig(type: TaskType | string | null | undefined): TaskTypeConfig {
  return TASK_TYPE_CONFIG[normalizeTaskType(type)];
}

/**
 * Get icon name for a task type
 */
export function getTaskTypeIcon(type: TaskType | string | null | undefined): string {
  return getTaskTypeConfig(type).icon;
}

/**
 * Get label for a task type
 */
export function getTaskTypeLabel(type: TaskType | string | null | undefined): string {
  return getTaskTypeConfig(type).label;
}

/**
 * Array of task type options for dropdowns/selects/segmented controls.
 */
export const TASK_TYPE_OPTIONS = Object.entries(TASK_TYPE_CONFIG).map(([value, config]) => ({
  value: value as TaskType,
  label: config.label,
  translationKey: config.translationKey,
  icon: config.icon,
  description: config.description,
}));
